/**
 * The shared register of places: every new venue or location goes through the
 * resolvePlace function, which reuses an existing place (same Google/OpenStreetMap
 * id, or same name nearby) and only creates one when nothing matches.
 */
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebaseConfig";

/**
 * @param {object} place { name, formattedAddress, address, city, zipCode, country, latitude, longitude,
 *                         googleId, osmId, timeZone, phoneNumber, website, capacity }
 * @param {object} opts  { artistId, asVenue, locationId } — locationId: put the venue on this existing location
 * @returns {Promise<{ locationId, venueId, venueName, timeZone, reused }>}
 */
export async function resolvePlace(place, { artistId = null, asVenue = false, locationId = null } = {}) {
    const res = await httpsCallable(functions, "resolvePlace")({ place, artistId, asVenue, locationId });
    return res.data;
}

const coord = (v) => (typeof v === "function" ? v() : typeof v === "number" ? v : v != null && v !== "" ? Number(v) : null);

/** A Google Places result (legacy or new API shape) as a place for resolvePlace. */
export function placeFromGoogle(g, overrides = {}) {
    const loc = g?.geometry?.location || g?.location || {};
    return {
        name: g?.name || g?.displayName?.text || g?.formatted_address || "",
        formattedAddress: g?.formatted_address || g?.formattedAddress || null,
        address: g?.formatted_address || g?.formattedAddress || null,
        city: g?.city || null,
        zipCode: g?.zipCode || null,
        country: g?.country || null,
        latitude: coord(loc.lat ?? loc.latitude),
        longitude: coord(loc.lng ?? loc.longitude),
        googleId: g?.place_id || g?.id || null,
        timeZone: g?.timezone || g?.timeZone || null,
        phoneNumber: g?.formatted_phone_number || g?.nationalPhoneNumber || null,
        website: g?.website || g?.websiteUri || null,
        ...overrides,
    };
}
